// Form rules for a task, apart from the component that draws them.
// They mirror data/schemas/task.js and data/models/work/task.js.

export const TITLE_MAX = 200;
export const CRITERION_MAX = 500;
export const SCOPE_MAX = 300;
export const QUESTION_MAX = 500;
export const QUESTIONS_MAX = 50;

export const TASK_KIND_OPTIONS = [
    { value: 'feature', label: 'Feature' },
    { value: 'fix',     label: 'Fix' },
    { value: 'test',    label: 'Test' },
];

// XL IS NOT OFFERED, and its absence is the point. The contract's enum is
// S, M and L, and its own description explains why: XL is not a size, it is a
// warning. A task that comes out XL has not been broken down, and it gets split
// before it goes in the queue. A fourth option here would turn a signal to
// split the work into a value somebody can just pick and move past.
export const ESTIMATE_OPTIONS = [
    { value: '',  label: 'Sin estimar' },
    { value: 'S', label: 'S', hint: 'Un archivo, o un cambio local' },
    { value: 'M', label: 'M', hint: 'Varios archivos, patrón ya existente' },
    { value: 'L', label: 'L', hint: 'Cruza capas o estrena un patrón' },
];

export const STATUS_OPTIONS = [
    { value: 2, label: 'Activo' },
    { value: 1, label: 'Inactivo' },
];

// READ ONLY, and this is the important one in the whole system.
//
// `workflow_state` is NOT in the PATCH body: a task does not move state
// because somebody edited a dropdown. It moves by being claimed, delivered,
// reviewed or returned — the atomic reservation of tier 2. A select here would
// be a way to say "done" without doing it.
//
// `proposed` gained a second meaning with the breakdown: EVERY task is born
// proposed now, so the state no longer separates a finding from a task a master
// wrote. Authorization is what moves one to `ready`, and only a person does it.
export const WORKFLOW_STATE_LABEL = {
    proposed:  'Propuesta',
    ready:     'Lista para tomar',
    claimed:   'Tomada',
    in_review: 'En revisión',
    done:      'Hecha',
    blocked:   'Bloqueada',
    returned:  'Devuelta',
    discarded: 'Descartada',
};

export const WORKFLOW_STATE_OPTIONS = Object.entries(WORKFLOW_STATE_LABEL)
    .map(([value, label]) => ({ value, label }));

export const EMPTY_VALUES = {
    tier_id: '',
    repository_id: '',
    kind: 'feature',
    title: '',
    description: '',
    acceptance_criteria: '',
    scope: '',
    open_questions: '',
    estimate: '',
    priority: 0,
    status: 2,
};

// `acceptance_criteria`, `scope` and `open_questions` are arrays in the
// contract and one item per line in the form. A line is what a person types; a
// JSON array is what the API takes, and translating between the two is this
// module's job.
function linesToArray(text) {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
    return lines.length ? lines : null;
}

function arrayToLines(value) {
    return Array.isArray(value) ? value.join('\n') : '';
}

export function valuesFrom(task) {
    return {
        tier_id: task?.tier_id != null ? String(task.tier_id) : '',
        repository_id: task?.repository_id != null ? String(task.repository_id) : '',
        kind: task?.kind ?? 'feature',
        title: task?.title ?? '',
        description: task?.description ?? '',
        acceptance_criteria: arrayToLines(task?.acceptance_criteria),
        scope: arrayToLines(task?.scope),
        open_questions: arrayToLines(task?.open_questions),
        estimate: task?.estimate ?? '',
        priority: task?.priority ?? 0,
        status: task?.status ?? 2,
    };
}

function tooLong(text, max) {
    return (linesToArray(text) ?? []).some(line => line.length > max);
}

export function validate(values, { withPlacement }) {
    const errors = {};
    const title = values.title.trim();

    if (withPlacement) {
        if (!values.tier_id) errors.tier_id = 'El tier es requerido';
        if (!values.repository_id) errors.repository_id = 'El repositorio es requerido';
    }

    if (!title) errors.title = 'El título es requerido';
    else if (title.length > TITLE_MAX) errors.title = `No puede pasar de ${TITLE_MAX} caracteres`;

    if (tooLong(values.acceptance_criteria, CRITERION_MAX)) {
        errors.acceptance_criteria = `Un criterio no puede pasar de ${CRITERION_MAX} caracteres`;
    }
    if (tooLong(values.scope, SCOPE_MAX)) {
        errors.scope = `Una ruta no puede pasar de ${SCOPE_MAX} caracteres`;
    }

    const questions = linesToArray(values.open_questions) ?? [];
    if (questions.length > QUESTIONS_MAX) {
        errors.open_questions = `No más de ${QUESTIONS_MAX} dudas`;
    } else if (questions.some(question => question.length > QUESTION_MAX)) {
        errors.open_questions = `Una duda no puede pasar de ${QUESTION_MAX} caracteres`;
    }

    if (Number(values.priority) < 0) errors.priority = 'El peso no puede ser negativo';

    return errors;
}

// THE SEVEN FIELDS THAT ARE THE SPECIFICATION.
//
// Changing any of them is changing what was asked for, so
// `PATCH /admin/tasks/{id}` demands the `spec_revision` that was read: without
// it 400, stale 409 (gate: revision), and an AUTHORIZED task 409 (gate:
// congelado) — because an authorization freezes the specification and its
// dependencies by trigger, through every door.
//
// `priority` and `status` are explicitly not specification and ask for nothing.
const SPEC_FIELDS = [
    'kind', 'title', 'description', 'acceptance_criteria', 'scope',
    'estimate', 'open_questions',
];

// Shared by create and update. `placement` (tier + repository) travels only on
// create: PATCH does not take either, because a task belongs to exactly ONE
// repository and moving it would break the answer to "which repo does the
// agent clone".
export function toCreatePayload(values) {
    const payload = {
        tier_id: Number(values.tier_id),
        repository_id: Number(values.repository_id),
        kind: values.kind,
        title: values.title.trim(),
        description: values.description.trim() || null,
        acceptance_criteria: linesToArray(values.acceptance_criteria),
        scope: linesToArray(values.scope),
        priority: Number(values.priority) || 0,
        status: Number(values.status),
    };

    if (values.estimate) payload.estimate = values.estimate;

    const questions = linesToArray(values.open_questions);
    if (questions) payload.open_questions = questions;

    return payload;
}

// ONLY WHAT CHANGED, plus the revision when the specification changed.
//
// AND `priority` IS NOT HERE ON PURPOSE. The weight has its own route —
// `PATCH /admin/tasks/{id}/weight` — because T03 asked for weight changes to be
// recorded, and a number buried inside a content PATCH is a change nobody can
// find afterwards. It also keeps the two apart where it matters: bumping the
// weight of an authorized task must not carry its frozen specification along
// for the ride. The field is read-only here until T07.07 builds that control.
export function toUpdatePayload(values, original, revision) {
    const from = valuesFrom(original);
    const payload = {};

    if (values.kind !== from.kind) payload.kind = values.kind;

    if (values.title.trim() !== from.title.trim()) {
        payload.title = values.title.trim();
    }
    if (values.description.trim() !== from.description.trim()) {
        payload.description = values.description.trim() || null;
    }
    if (values.acceptance_criteria.trim() !== from.acceptance_criteria.trim()) {
        payload.acceptance_criteria = linesToArray(values.acceptance_criteria);
    }
    if (values.scope.trim() !== from.scope.trim()) {
        payload.scope = linesToArray(values.scope);
    }
    if (values.open_questions.trim() !== from.open_questions.trim()) {
        payload.open_questions = linesToArray(values.open_questions) ?? [];
    }
    if (values.estimate !== from.estimate) {
        payload.estimate = values.estimate || null;
    }

    if (Number(values.status) !== Number(from.status)) payload.status = Number(values.status);

    const touchedSpec = SPEC_FIELDS.some(field => field in payload);
    if (touchedSpec && revision != null) payload.spec_revision = revision;

    return payload;
}

export function hasChanges(payload) {
    return Object.keys(payload).some(key => key !== 'spec_revision');
}

// The three questions creating a task answers, in the order somebody answers
// them. Not a split by field count: each step is a different head.
//
// Step 1 is first because it is the one that CANNOT be changed later — PATCH
// takes neither tier_id nor repository_id. Asking it up front, alone, is the
// difference between a decision and a slip.
export const CREATE_STEPS = [
    { title: 'Dónde vive',  fields: ['tier_id', 'repository_id'] },
    { title: 'Qué hacer',   fields: ['title', 'kind', 'estimate', 'priority', 'description'] },
    { title: 'Cómo se acepta', fields: ['acceptance_criteria', 'scope', 'open_questions', 'status'] },
];
